import React from 'react';
import { TrendingUp, Target, Clock, CheckCircle2 } from 'lucide-react';

interface ProgressBarProps {
  value: number;
  max?: number;
  className?: string;
  showLabel?: boolean;
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'purple';
  size?: 'sm' | 'md' | 'lg';
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  className = '',
  showLabel = true,
  color = 'blue',
  size = 'md'
}) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  
  const colorClasses = {
    blue: 'bg-accent',
    green: 'bg-success-soft',
    yellow: 'bg-warning-soft',
    red: 'bg-danger-soft',
    purple: 'bg-accent'
  };

  const sizeClasses = {
    sm: 'h-1',
    md: 'h-2',
    lg: 'h-3'
  };

  return (
    <div className={`w-full ${className}`}>
      {showLabel && (
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs font-medium text-txt-secondary">
            {Math.round(percentage)}%
          </span>
        </div>
      )}
      <div className={`w-full bg-bg-surface-hover rounded-full overflow-hidden ${sizeClasses[size]}`}>
        <div
          className={`${colorClasses[color]} ${sizeClasses[size]} rounded-full transition-all duration-500 ease-out`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Lead Score Indicator
interface LeadScoreProps {
  score: number;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

export const LeadScore: React.FC<LeadScoreProps> = ({ score, size = 'md', showIcon = true }) => {
  const getColor = (score: number): 'red' | 'yellow' | 'green' => {
    if (score >= 70) return 'green';
    if (score >= 40) return 'yellow';
    return 'red';
  };

  const getSizeClasses = (size: string) => {
    const sizes = {
      sm: { container: 'space-y-1', text: 'text-xs', icon: 'w-3 h-3' },
      md: { container: 'space-y-1.5', text: 'text-sm', icon: 'w-4 h-4' },
      lg: { container: 'space-y-2', text: 'text-base', icon: 'w-5 h-5' }
    };
    return sizes[size as keyof typeof sizes];
  };

  const sizeClasses = getSizeClasses(size);
  const color = getColor(score);

  return (
    <div className={sizeClasses.container}>
      {showIcon && (
        <div className="flex items-center space-x-2">
          <TrendingUp className={`${sizeClasses.icon} text-txt-secondary`} />
          <span className={`${sizeClasses.text} font-medium text-txt-secondary`}>
            Lead Score
          </span>
        </div>
      )}
      <ProgressBar 
        value={score} 
        max={100} 
        color={color}
        size={size}
        showLabel={true}
      />
    </div>
  );
};

// Deal Progress (Opportunity Stages)
interface DealProgressProps {
  currentStage: string;
  stages: string[];
  size?: 'sm' | 'md' | 'lg';
}

export const DealProgress: React.FC<DealProgressProps> = ({ 
  currentStage, 
  stages,
  size = 'md'
}) => {
  const currentIndex = stages.indexOf(currentStage);
  const progress = ((currentIndex + 1) / stages.length) * 100;

  const sizeClasses = {
    sm: { dot: 'w-2 h-2', text: 'text-xs', spacing: 'space-x-1' },
    md: { dot: 'w-3 h-3', text: 'text-sm', spacing: 'space-x-2' },
    lg: { dot: 'w-4 h-4', text: 'text-base', spacing: 'space-x-3' }
  };

  const classes = sizeClasses[size];

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <Target className="w-4 h-4 text-txt-secondary" />
          <span className={`${classes.text} font-medium text-txt-secondary`}>
            Deal Progress
          </span>
        </div>
        <span className={`${classes.text} text-txt-secondary`}>
          {currentIndex + 1}/{stages.length}
        </span>
      </div>
      
      {/* Progress Bar */}
      <div className="relative">
        <div className="w-full bg-bg-surface-hover rounded-full h-2">
          <div
            className="bg-accent h-2 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Stage Dots */}
        <div className={`flex justify-between items-center mt-3 ${classes.spacing}`}>
          {stages.map((stage, index) => {
            const isPast = index < currentIndex;
            const isCurrent = index === currentIndex;
            const isFuture = index > currentIndex;

            return (
              <div key={stage} className="flex flex-col items-center flex-1">
                <div
                  className={`${classes.dot} rounded-full transition-all duration-300 ${
                    isPast
                      ? 'bg-accent'
                      : isCurrent
                      ? 'bg-accent ring-4 ring-accent/30'
                      : 'bg-bg-surface-hover'
                  }`}
                />
                <span
                  className={`mt-1 ${classes.text} capitalize truncate max-w-full ${
                    isCurrent
                      ? 'font-semibold text-accent'
                      : isPast
                      ? 'text-txt-secondary'
                      : 'text-txt-muted'
                  }`}
                >
                  {stage}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Task Completion Badge
interface TaskCompletionProps {
  completed: number;
  total: number;
  size?: 'sm' | 'md' | 'lg';
}

export const TaskCompletion: React.FC<TaskCompletionProps> = ({ 
  completed, 
  total,
  size = 'md'
}) => {
  const percentage = total > 0 ? (completed / total) * 100 : 0;

  const sizeClasses = {
    sm: { badge: 'px-2 py-1 text-xs', icon: 'w-3 h-3' },
    md: { badge: 'px-3 py-1.5 text-sm', icon: 'w-4 h-4' },
    lg: { badge: 'px-4 py-2 text-base', icon: 'w-5 h-5' }
  };

  const classes = sizeClasses[size];

  const getBgColor = (pct: number) => {
    if (pct === 100) return 'bg-success-soft text-success border-success';
    if (pct >= 50) return 'bg-info-soft text-accent border-accent';
    if (pct > 0) return 'bg-warning-soft text-warning border-warning';
    return 'bg-bg-surface text-txt-secondary border-default';
  };

  return (
    <div className={`inline-flex items-center ${classes.badge} rounded-full border ${getBgColor(percentage)} font-medium`}>
      {percentage === 100 ? (
        <CheckCircle2 className={`${classes.icon} mr-1.5`} />
      ) : (
        <Clock className={`${classes.icon} mr-1.5`} />
      )}
      <span>{completed}/{total}</span>
      {total > 0 && (
        <span className="ml-1.5 opacity-75">({Math.round(percentage)}%)</span>
      )}
    </div>
  );
};

// Circular Progress (for dashboard widgets)
interface CircularProgressProps {
  value: number;
  max?: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  backgroundColor?: string;
  children?: React.ReactNode;
}

export const CircularProgress: React.FC<CircularProgressProps> = ({
  value,
  max = 100,
  size = 120,
  strokeWidth = 8,
  color = '#3b82f6',
  backgroundColor = '#e5e7eb',
  children
}) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        {children || (
          <span className="text-2xl font-bold text-txt">
            {Math.round(percentage)}%
          </span>
        )}
      </div>
    </div>
  );
};

